/**
 * Per-tick reduction of the encoded video frames a guest receives, by size.
 *
 * Why size: libwebrtc's jitter estimator sets its buffer from
 * `theta * (max_frame_size - avg_frame_size) + noise`, with `theta` a Kalman-learned
 * "ms per byte" and `max_frame_size` a peak tracker decaying at ~0.9999 per frame. The
 * 2026-09-20 Android session ramped its target 55 -> 285 ms with smooth arrivals, no loss,
 * a 9 ms decode and one keyframe in 160 s - which leaves this term. Putting the frame-size
 * spread on the wire is how the hypothesis gets confirmed or killed.
 *
 * Browser-free: the encoded transform that sees frames lives in the app (it needs a Worker);
 * this tracker only folds what it reports. Batches, not frames, cross the worker boundary.
 */

/** One batch of frames the transform saw, already folded on the worker side. */
export type EncodedFrameBatch = {
  frames: number;
  sumBytes: number;
  maxBytes: number;
  keyframes: number;
  /** Largest keyframe in the batch, 0 when none. */
  maxKeyframeBytes: number;
};

/** The wire summary: one tick of encoded frames. */
export type EncodedFrameSummary = {
  frames: number;
  avgBytes: number;
  maxBytes: number;
  keyframes: number;
  /** Largest keyframe this tick; omitted when no keyframe arrived. */
  maxKeyframeBytes?: number;
};

export function isEncodedFrameBatch(value: unknown): value is EncodedFrameBatch {
  if (value === null || typeof value !== 'object') return false;
  const b = value as Record<string, unknown>;
  return ['frames', 'sumBytes', 'maxBytes', 'keyframes', 'maxKeyframeBytes'].every(
    (k) => typeof b[k] === 'number' && Number.isFinite(b[k] as number) && (b[k] as number) >= 0,
  );
}

export function isEncodedFrameSummary(value: unknown): value is EncodedFrameSummary {
  if (value === null || typeof value !== 'object') return false;
  const s = value as Record<string, unknown>;
  const finite = (v: unknown): boolean => typeof v === 'number' && Number.isFinite(v) && v >= 0;
  if (!['frames', 'avgBytes', 'maxBytes', 'keyframes'].every((k) => finite(s[k]))) return false;
  return s['maxKeyframeBytes'] === undefined || finite(s['maxKeyframeBytes']);
}

/** Accumulates worker batches between clientStats sends and reduces them to one summary. */
export class EncodedFrameSizeTracker {
  private frames = 0;
  private sumBytes = 0;
  private maxBytes = 0;
  private keyframes = 0;
  private maxKeyframeBytes = 0;

  observeBatch(batch: EncodedFrameBatch): void {
    if (batch.frames <= 0) return;
    this.frames += batch.frames;
    this.sumBytes += batch.sumBytes;
    this.maxBytes = Math.max(this.maxBytes, batch.maxBytes);
    this.keyframes += batch.keyframes;
    this.maxKeyframeBytes = Math.max(this.maxKeyframeBytes, batch.maxKeyframeBytes);
  }

  /** Undefined when no frame arrived since the last call - omitted, never zero-filled. */
  summarizeAndReset(): EncodedFrameSummary | undefined {
    if (this.frames === 0) return undefined;
    const summary: EncodedFrameSummary = {
      frames: this.frames,
      avgBytes: Math.round(this.sumBytes / this.frames),
      maxBytes: this.maxBytes,
      keyframes: this.keyframes,
      ...(this.keyframes > 0 ? { maxKeyframeBytes: this.maxKeyframeBytes } : {}),
    };
    this.reset();
    return summary;
  }

  reset(): void {
    this.frames = 0;
    this.sumBytes = 0;
    this.maxBytes = 0;
    this.keyframes = 0;
    this.maxKeyframeBytes = 0;
  }
}

/**
 * The worker-side fold: one frame into a batch. Kept here so the worker and the tests share
 * the arithmetic and the worker itself stays a dozen lines of plumbing.
 */
export function foldEncodedFrame(batch: EncodedFrameBatch, bytes: number, isKeyframe: boolean): void {
  batch.frames += 1;
  batch.sumBytes += bytes;
  if (bytes > batch.maxBytes) batch.maxBytes = bytes;
  if (isKeyframe) {
    batch.keyframes += 1;
    if (bytes > batch.maxKeyframeBytes) batch.maxKeyframeBytes = bytes;
  }
}

export function emptyEncodedFrameBatch(): EncodedFrameBatch {
  return { frames: 0, sumBytes: 0, maxBytes: 0, keyframes: 0, maxKeyframeBytes: 0 };
}
